/**
 * Resolves the entity_id of the terminal stage for a task, scoped to the
 * pipeline that owns the task's current stage. Uses each stage's `is_terminal`
 * flag — returns null for pipelines with no terminal stage. When multiple
 * terminal stages exist, prefers one named "completed" or "done"; otherwise
 * falls back to the terminal stage with the highest order value.
 */

import { useCallback, useMemo } from 'react';

import type { Pipeline } from '@/lib/crm/hooks/usePipelines';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';

/**
 * Pure resolution logic, kept separate from data fetching so it can be unit
 * tested without mounting `usePipelines`.
 */
export function resolveCompletedStageEntityId(
  pipelines: Pipeline[],
  currentStageEntityId: string | null | undefined
): string | null {
  if (pipelines.length === 0) {
    return null;
  }
  // Resolve within the pipeline that owns the task's current stage; fall
  // back to the default pipeline (identical for seeded single-pipeline orgs,
  // correct for orgs with custom task pipelines).
  const owningPipeline =
    (currentStageEntityId != null
      ? pipelines.find(p =>
          p.stages.some(s => s.entity_id === currentStageEntityId)
        )
      : undefined) ??
    pipelines.find(p => p.is_default) ??
    pipelines[0];
  if (!owningPipeline) {
    return null;
  }
  const terminalStages = owningPipeline.stages.filter(
    s => s.entity_id != null && s.is_terminal === true
  );
  if (terminalStages.length === 0) {
    return null;
  }
  if (terminalStages.length === 1) {
    return terminalStages[0].entity_id ?? null;
  }
  // Multiple terminal stages: prefer one named "completed" or "done".
  const norm = (v: string | undefined) => (v ?? '').trim().toLowerCase();
  const PREFERRED = new Set(['completed', 'done']);
  const matched = terminalStages.find(
    s => PREFERRED.has(norm(s.stage_name)) || PREFERRED.has(norm(s.label))
  );
  if (matched) {
    return matched.entity_id ?? null;
  }
  // Fall back to the terminal stage with the highest order value.
  const highest = terminalStages.reduce((best, s) =>
    s.properties.order > best.properties.order ? s : best
  );
  return highest.entity_id ?? null;
}

export interface CompletedTaskStageState {
  /**
   * The resolved "Completed" stage entity_id, or null while loading, when
   * disabled, on a fetch error, or if it cannot be resolved.
   */
  completedStageEntityId: string | null;
  /** The pipeline fetch is in flight (distinguish loading from a failure). */
  isLoading: boolean;
  /** The pipeline fetch failed — resolution is unavailable, not merely slow. */
  isError: boolean;
  /** Retry the pipeline fetch (e.g. from an error/retry affordance). */
  refetch: () => void;
}

/**
 * Resolves the "Completed" stage for the given task's pipeline, plus the fetch
 * state needed to tell "still loading" apart from "failed to load" so a caller
 * can offer a retry instead of treating a failure as a permanent loading state.
 */
export function useCompletedTaskStage(
  orgId: number | undefined,
  currentStageEntityId: string | null | undefined,
  enabled: boolean
): CompletedTaskStageState {
  const { pipelines, isLoading, error, refetch } = usePipelines(
    enabled ? orgId : undefined,
    'ahoy_task'
  );

  const completedStageEntityId = useMemo(() => {
    if (!enabled || isLoading || pipelines.length === 0) {
      return null;
    }
    return resolveCompletedStageEntityId(pipelines, currentStageEntityId);
  }, [enabled, isLoading, pipelines, currentStageEntityId]);

  const stableRefetch = useCallback(() => {
    void refetch();
  }, [refetch]);

  return useMemo(
    () => ({
      completedStageEntityId,
      isLoading: enabled && isLoading,
      isError: enabled && error != null,
      refetch: stableRefetch,
    }),
    [completedStageEntityId, enabled, isLoading, error, stableRefetch]
  );
}
